'use client';

import { css } from 'styled-components';
import type { Brand } from '@/domainLogic/index.ts';
import { HEADER_HEIGHT, SEARCH_WIDGET_HEIGHT } from '@/app/common/constants.ts';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import customCss from '@/app/components/hotel-detail/css/customCss.ts';

// The fixed ContentHub header on the non-evo detail steps.
const CONTENT_HUB_HEADER_HEIGHT = 64;

/**
 * `top` offset the sticky detail-page elements need to clear the fixed header band.
 * For most brands that is the 64px ContentHub header; for the external-header brands
 * (HP/HG) the band is the brand header plus the CSW it carries on the detail steps
 * too (MPH-864), mirroring HeaderContainer's heights in `src/app/common/styled.ts`.
 * In the app the header is hidden.
 *
 * `stickyTop` is the numeric offset for IntersectionObserver rootMargins: it picks the
 * header height by the JS isMobile flag (767px), close enough for the pin detection.
 * `stickyTopCss` is what positions: it switches at the same breakpoint as the header
 * band itself (customCss.breakpoints.sm).
 */
export const useStickyHeaderOffset = () => {
  const { externalHeaderEnabled, isMobile } = useCommonClient();
  const {
    commonData: { isApp },
    localization,
  } = usePageWrapperData();
  const brand = localization.brand as Brand;

  if (isApp) {
    return {
      stickyTop: 0,
      stickyTopCss: css`
        top: 0;
      `,
    };
  }

  if (!externalHeaderEnabled) {
    return {
      stickyTop: CONTENT_HUB_HEADER_HEIGHT,
      stickyTopCss: css`
        top: ${CONTENT_HUB_HEADER_HEIGHT}px;
      `,
    };
  }

  return {
    stickyTop: HEADER_HEIGHT(brand, isMobile) + SEARCH_WIDGET_HEIGHT,
    stickyTopCss: css`
      top: ${HEADER_HEIGHT(brand, true) + SEARCH_WIDGET_HEIGHT}px;

      @media ${customCss.breakpoints.sm} {
        top: ${HEADER_HEIGHT(brand, false) + SEARCH_WIDGET_HEIGHT}px;
      }
    `,
  };
};
